import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  Collapse,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { Dayjs } from "dayjs";
import type { Hierarchy, createFlashService } from "@/api/flash.service";
import { getUserCompanies } from "@/api/user.service";
import type { Company } from "@/api/company.service";
import { formatCurrency, formatPercentage } from "@/utils/format";

export type FlashService = ReturnType<typeof createFlashService>;

interface HierarchyPanelProps {
  flashService: FlashService;
  clientId: string;
  clientName: string;
  userId: string;
  currentDate: Dayjs;
  isDiario?: boolean;
}

const kpiRows = (item: Hierarchy, isDiario: boolean) => [
  ...(!isDiario && item.margem_atual !== undefined
    ? [{ label: "% Margem Atual", value: formatPercentage(item.margem_atual), raw: item.margem_atual }]
    : []),
  { label: isDiario ? "Meta Dia" : "Meta", value: formatCurrency(item.meta_mes), raw: item.meta_mes },
  { label: isDiario ? "Dia Atual" : "Mês Atual", value: formatCurrency(item.mes_atual), raw: item.mes_atual },
  ...(!isDiario && item.perc_perda !== undefined
    ? [{ label: "% Perda", value: formatPercentage(item.perc_perda), raw: item.perc_perda }]
    : []),
  ...(!isDiario && item.perc_meta !== undefined
    ? [{ label: "% Meta", value: formatPercentage(item.perc_meta), raw: item.perc_meta }]
    : []),
  { label: "Dif ($)", value: formatCurrency(item.dif_mes_atual), raw: item.dif_mes_atual },
  { label: "% Variação", value: formatPercentage(item.variacao_atual), raw: item.variacao_atual },
  ...(!isDiario && item.margem_mes_ante !== undefined
    ? [{ label: "% Margem Mês Anterior", value: formatPercentage(item.margem_mes_ante), raw: item.margem_mes_ante }]
    : []),
  { label: isDiario ? "Dia M. Ant." : "Mês Anterior", value: formatCurrency(item.mes_anterior), raw: item.mes_anterior },
  { label: "Dif ($)", value: formatCurrency(item.dif_mes_anterior), raw: item.dif_mes_anterior },
  { label: "% Variação", value: formatPercentage(item.variacao_mes_anterior), raw: item.variacao_mes_anterior },
  ...(!isDiario && item.margem_ano_ante !== undefined
    ? [{ label: "% Margem Ano Anterior", value: formatPercentage(item.margem_ano_ante), raw: item.margem_ano_ante }]
    : []),
  { label: isDiario ? "Dia A. Ant." : "Ano Anterior", value: formatCurrency(item.ano_anterior), raw: item.ano_anterior },
  { label: "Dif ($)", value: formatCurrency(item.dif_ano_anterior), raw: item.dif_ano_anterior },
  { label: "% Variação", value: formatPercentage(item.variacao_ano_anterior), raw: item.variacao_ano_anterior },
];

function HierarchyCard({
  name,
  item,
  expanded,
  onToggle,
  buttonLabel,
  onButtonClick,
  depth = 0,
  isDiario = false,
}: {
  name: string;
  item: Hierarchy;
  expanded: boolean;
  onToggle: () => void;
  buttonLabel?: string;
  onButtonClick?: () => void;
  depth?: number;
  isDiario?: boolean;
}) {
  const percent = item.meta_mes > 0 ? Math.min((item.mes_atual / item.meta_mes) * 100, 100) : 0;

  return (
    <Card
      variant="outlined"
      sx={{
        ml: depth * 3,
        ...(depth > 0 && { bgcolor: "background.subtle", borderLeftWidth: 3, borderLeftColor: "primary.light" }),
      }}
    >
      <CardActionArea onClick={onToggle} sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          {expanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
          <Typography variant="subtitle2" sx={{ flex: 1, textTransform: "uppercase", color: "text.secondary" }}>
            {name}
          </Typography>
          <Typography variant="h6" sx={{ color: item.mes_atual < 0 ? "error.main" : "text.primary" }}>
            {formatCurrency(item.mes_atual)}
          </Typography>
        </Stack>

        <Collapse in={expanded} sx={{ width: "100%" }}>
          <Box
            sx={{
              mt: 2,
              display: "grid",
              gridTemplateColumns: "max-content 1fr max-content 1fr",
              columnGap: 3,
              rowGap: 0.75,
              bgcolor: depth > 0 ? "background.paper" : "background.subtle",
              borderRadius: 2,
              p: 1.5,
            }}
          >
            {kpiRows(item, isDiario).map((row, idx) => (
              <Fragment key={idx}>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                  {row.label}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  textAlign="right"
                  sx={{ color: row.raw < 0 ? "error.main" : "text.primary" }}
                >
                  {row.value}
                </Typography>
              </Fragment>
            ))}
          </Box>
          <LinearProgress variant="determinate" value={percent} sx={{ mt: 1.5, borderRadius: 1, height: 5 }} />
        </Collapse>
      </CardActionArea>

      {expanded && onButtonClick && (
        <Box
          sx={{ borderTop: "1px solid", borderColor: "divider", textAlign: "center", py: 1, cursor: "pointer" }}
          onClick={onButtonClick}
        >
          <Typography variant="caption" fontWeight={600} color="primary.main">
            {buttonLabel ?? `Ver categorias de ${name.toUpperCase()} →`}
          </Typography>
        </Box>
      )}
    </Card>
  );
}

function CategoryNode({
  cat,
  flashService,
  dateISO,
  companyCode,
  clientId,
  depth,
  isDiario = false,
}: {
  cat: Hierarchy;
  flashService: FlashService;
  dateISO: string;
  companyCode: string | null;
  clientId: string;
  depth: number;
  isDiario?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [children, setChildren] = useState<Hierarchy[] | null>(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && children === null) {
      setLoading(true);
      try {
        const data = isDiario
          ? await flashService.getCategoriasDiario(dateISO, companyCode, cat.cod_empresa, clientId)
          : await flashService.getCategorias(dateISO, companyCode, cat.cod_empresa, clientId);
        setChildren(data.filter((c) => !c.is_total));
      } finally {
        setLoading(false);
      }
    }
  };

  const subCats = (children ?? []).filter((c) => c.canalapp !== "PRODUTO");
  const products = (children ?? []).filter((c) => c.canalapp === "PRODUTO");

  return (
    <Stack gap={1}>
      <HierarchyCard name={cat.nomeempresaapp} item={cat} expanded={expanded} onToggle={toggle} depth={depth} isDiario={isDiario} />
      {expanded && loading && <LinearProgress sx={{ ml: depth * 3 }} />}
      {expanded && products.length > 0 && (
        <Stack sx={{ ml: (depth + 1) * 3 }} gap={0.5}>
          {products.map((p, idx) => (
            <Stack key={idx} direction="row" justifyContent="space-between" sx={{ px: 2, py: 0.75, bgcolor: "background.subtle", borderRadius: 1 }}>
              <Typography variant="body2">{p.nomeempresaapp}</Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatCurrency(p.mes_atual)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
      {expanded &&
        subCats.map((sub, idx) => (
          <CategoryNode
            key={idx}
            cat={sub}
            flashService={flashService}
            dateISO={dateISO}
            companyCode={companyCode}
            clientId={clientId}
            depth={depth + 1}
            isDiario={isDiario}
          />
        ))}
    </Stack>
  );
}

export function HierarchyPanel({ flashService, clientId, clientName, userId, currentDate, isDiario = false }: HierarchyPanelProps) {
  const [tab, setTab] = useState(0);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [hierarchyData, setHierarchyData] = useState<Hierarchy[]>([]);
  const [categoriesData, setCategoriesData] = useState<Hierarchy[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedTotal, setExpandedTotal] = useState(true);
  const [expandedLoja, setExpandedLoja] = useState<Record<string, boolean>>({});
  const [selectedCompanyCode, setSelectedCompanyCode] = useState<string | null>(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string | null>(null);

  const dateYMD = currentDate.format("YYYYMMDD");
  const dateISO = currentDate.format("YYYY-MM-DD");

  useEffect(() => {
    getUserCompanies(userId).then((data) => setCompanies(data.filter((c) => c.client_id === clientId)));
  }, [userId, clientId]);

  useEffect(() => {
    setLoading(true);
    const request = isDiario ? flashService.getHierarchyDiario(dateISO, clientId) : flashService.getHierarchy(dateYMD, clientId);
    request.then(setHierarchyData).finally(() => setLoading(false));
  }, [dateYMD, dateISO, clientId, isDiario]);

  useEffect(() => {
    if (tab !== 1) return;
    setLoading(true);
    const request = isDiario
      ? flashService.getCategoriasDiario(dateISO, selectedCompanyCode, null, clientId)
      : flashService.getCategorias(dateISO, selectedCompanyCode, null, clientId);
    request.then((data) => setCategoriesData(data.filter((c) => !c.is_total))).finally(() => setLoading(false));
  }, [tab, dateISO, selectedCompanyCode, clientId, isDiario]);

  const resolveCompanyName = (codEmpresa: string, fallback: string) =>
    companies.find((c) => c.erp_code === codEmpresa)?.name ?? fallback;

  const total = hierarchyData.find((i) => i.is_total);
  const lojas = useMemo(
    () =>
      hierarchyData
        .filter((i) => !i.is_total && i.nomeempresaapp)
        .sort((a, b) => resolveCompanyName(a.cod_empresa, a.nomeempresaapp).localeCompare(resolveCompanyName(b.cod_empresa, b.nomeempresaapp), "pt-BR")),
    [hierarchyData, companies],
  );

  const goToCategorias = (companyCode: string | null, companyName: string | null) => {
    setSelectedCompanyCode(companyCode);
    setSelectedCompanyName(companyName);
    setTab(1);
  };

  return (
    <Stack gap={2}>
      <Tabs
        value={tab}
        onChange={(_, v) => {
          if (v === 0) {
            setSelectedCompanyCode(null);
            setSelectedCompanyName(null);
          }
          setTab(v);
        }}
      >
        <Tab label="Estrutura" />
        <Tab label="Categorias" />
      </Tabs>

      {tab === 1 && selectedCompanyName && (
        <Typography variant="body2" fontWeight={700} sx={{ px: 1 }}>
          {selectedCompanyName}
        </Typography>
      )}

      {loading ? (
        <LinearProgress />
      ) : tab === 0 ? (
        !total ? (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 6 }}>
            Nenhum dado encontrado para o período selecionado.
          </Typography>
        ) : (
          <Stack gap={1}>
            <HierarchyCard
              name={clientName}
              item={total}
              expanded={expandedTotal}
              onToggle={() => setExpandedTotal((v) => !v)}
              onButtonClick={() => goToCategorias(null, null)}
              isDiario={isDiario}
            />
            {expandedTotal &&
              lojas.map((loja, idx) => {
                const key = `${loja.cod_empresa}-${idx}`;
                const lojaName = resolveCompanyName(loja.cod_empresa, loja.nomeempresaapp);
                return (
                  <HierarchyCard
                    key={key}
                    name={lojaName}
                    item={loja}
                    depth={1}
                    expanded={!!expandedLoja[key]}
                    onToggle={() => setExpandedLoja((prev) => ({ ...prev, [key]: !prev[key] }))}
                    onButtonClick={() => goToCategorias(loja.cod_empresa, lojaName)}
                    isDiario={isDiario}
                  />
                );
              })}
          </Stack>
        )
      ) : categoriesData.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" sx={{ py: 6 }}>
          Nenhuma categoria encontrada para o período selecionado.
        </Typography>
      ) : (
        <Stack gap={1}>
          {categoriesData.map((cat, idx) => (
            <CategoryNode
              key={idx}
              cat={cat}
              flashService={flashService}
              dateISO={dateISO}
              companyCode={selectedCompanyCode}
              clientId={clientId}
              depth={0}
              isDiario={isDiario}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
